/**
 * Los pasos de la PUESTA EN MARCHA: el flujo por el que se lleva al usuario
 * para dejar el programa andando.
 *
 * ESTO ES UNA LISTA Y LA PANTALLA NO SABE NADA MÁS. El paso actual, el total,
 * el "¿hay uno después?", el título grande y la bajada se DERIVAN de este
 * array: nunca se escriben a mano en el JSX. Agregar el paso 3 es agregar un
 * objeto acá (y su componente en el registro de la pantalla) — no volver a
 * tocar el encabezado, ni el indicador de progreso, ni el pie con el botón. Un
 * `total` escrito a mano en el JSX es exactamente el lugar donde el paso nuevo
 * se agrega y el indicador sigue diciendo "de 1"; un `<h1>` escrito a mano es
 * el lugar donde el paso 2 se agrega y el título sigue siendo el del paso 1
 * (que es justo lo que pasó y por lo que el encabezado se mudó acá).
 *
 * El id NO es el índice: el orden se puede cambiar, se puede meter un paso en
 * el medio, y lo que se guarde en el futuro (por dónde iba el usuario) tiene
 * que seguir apuntando al mismo paso.
 *
 * Ver ADR-2026-08-17-estudio-marketing-puesta-en-marcha y
 * ADR-2026-08-17-estudio-marketing-paso-sistema-diseno.
 */

export type IdPasoPuestaEnMarcha = "conexiones" | "sistema-diseno" | "meta-ads";

export interface PasoPuestaEnMarcha {
  id: IdPasoPuestaEnMarcha;
  /** El nombre del paso, tal como lo diría un indicador de progreso. */
  titulo: string;
  /** El `<h1>` de la pantalla mientras se está en este paso. */
  encabezado: string;
  /** La línea debajo del `<h1>`, o `null` si el paso no lleva. */
  bajada: string | null;
  /** Qué le contamos al chat de Atlas sobre este paso cuando se lo abre desde
   *  acá. Va con el paso y no en la pantalla porque el chat tiene que hablar
   *  del paso que se está viendo, no del primero del flujo. */
  detalleChat: string;
}

/** El flujo, en orden. Agregar un paso es agregar una línea acá. */
export const PASOS_PUESTA_EN_MARCHA: readonly PasoPuestaEnMarcha[] = [
  {
    id: "conexiones",
    titulo: "Conectá tus fuentes",
    // Este encabezado —y que vaya SIN bajada— lo eligió Matías por pedido
    // explícito. NO REVERTIR.
    encabezado: "Empecemos por darle conocimiento a tu agente",
    bajada: null,
    detalleChat:
      "Flujo de configuración inicial: conectar Instagram, Meta Ads, Tiendanube y canales de creadores.",
  },
  {
    id: "sistema-diseno",
    titulo: "Armá tu sistema de diseño",
    encabezado: "Armemos tu sistema de diseño",
    bajada: "Contanos de tu marca y sumá los recursos de diseño que tengas.",
    detalleChat:
      "Segundo paso de la puesta en marcha: cargar el sistema de diseño (repos de GitHub, código, archivos .fig, tipografías, logos y assets).",
  },
  {
    id: "meta-ads",
    titulo: "Armá tu estrategia de Meta Ads",
    // Sin "Meta Ads" en la bajada: adentro del paso, el título de la pantalla
    // que sigue ya lo dice ("Contame cómo venías trabajando Meta Ads hasta
    // ahora") y repetirlo dos veces en la misma vista es ruido.
    encabezado: "Tu estrategia de Meta Ads",
    bajada: "Con qué contexto querés que Atlas la arme.",
    detalleChat:
      "Tercer paso de la puesta en marcha: definir la estrategia de Meta Ads. Se pregunta si ya venía haciendo campañas; quien dice que sí cuenta cómo venía trabajando, y cualquiera puede delegarle a Atlas que la arme.",
  },
];

export interface ProgresoPaso {
  /** 1-based: es lo que se muestra ("Paso 1 de 3"), no un índice. */
  numero: number;
  /** Sale del largo de la lista. Nunca de una constante aparte. */
  total: number;
  paso: PasoPuestaEnMarcha;
}

/**
 * Dónde está parado el usuario. `null` cuando el id no está en la lista —el
 * caso de un paso borrado o de un estado guardado que quedó viejo—, y NO un
 * progreso inventado: `indexOf` devuelve `-1` ante un id desconocido y un
 * "Paso 0 de 1" es peor que no dibujar nada.
 *
 * Recibe la lista por parámetro (en vez de leer la constante) para que se pueda
 * testear con flujos de varios pasos sin esperar a que el flujo real los tenga.
 */
export function progresoDePaso(
  pasos: readonly PasoPuestaEnMarcha[],
  id: string,
): ProgresoPaso | null {
  const indice = pasos.findIndex((paso) => paso.id === id);
  if (indice === -1) return null;
  return { numero: indice + 1, total: pasos.length, paso: pasos[indice] };
}

/**
 * El paso que sigue, o `null` si éste es el último.
 *
 * De acá sale una de las dos cosas que la pantalla NO puede dibujar mal: en el
 * ÚLTIMO paso "Continuar" no tiene a dónde llevar y la interfaz lo tiene que
 * decir antes de que lo aprieten. El pie no sabe cuál es el último: pregunta.
 */
export function pasoSiguiente(
  pasos: readonly PasoPuestaEnMarcha[],
  id: string,
): PasoPuestaEnMarcha | null {
  const indice = pasos.findIndex((paso) => paso.id === id);
  if (indice === -1) return null;
  return pasos[indice + 1] ?? null;
}

/**
 * El paso anterior, o `null` si éste es el primero.
 *
 * Existe porque el flujo dejó de tener un paso solo: sin vuelta atrás, quien
 * avanza queda encerrado en el último —el ítem de la sidebar no remonta la
 * pantalla, así que ni siquiera salir y volver lo saca de ahí—. `indice - 1`
 * con `indice === 0` da `-1`, que en un array devuelve `undefined` y no el
 * último elemento; el `?? null` lo convierte en el `null` que la pantalla
 * pregunta.
 */
export function pasoAnterior(
  pasos: readonly PasoPuestaEnMarcha[],
  id: string,
): PasoPuestaEnMarcha | null {
  const indice = pasos.findIndex((paso) => paso.id === id);
  if (indice <= 0) return null;
  return pasos[indice - 1] ?? null;
}
